import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Panel, InputGroup } from 'react-bootstrap';
import { navigate } from 'raviger';
import _ from 'lodash';

import withAuthentication from '../../hoc/withAuthentication';
import withLayout from '../../hoc/withLayout';
import Permission from '../../components/Permission/Permission';
import FormInput from '../../components/FormInput/FormInput';
import Spinner from '../../components/Spinner/Spinner';
import OptionsHelper from '../../lib/OptionsHelper';
import { getListState, saveListState } from '../../lib/ListStateHelper';

import { useAlertStore } from '../../store/providers/Alert';
import { useMailMergeTemplateStore } from '../../store/providers/MailMergeTemplate';
import { useMailMergeCategoryStore } from '../../store/providers/MailMergeCategory';
import { breadcrumbAddRoute, breadcrumbRemoveRoute } from '../../store/actions/breadcrumb';

import * as constants from '../../constants';
import { columns, sorting } from './MailMergeTemplateListSchema';

const DocumentSetupList = React.lazy(() => import('../../components/Lists/DocumentSetupList/DocumentSetupList'));
const SideMenu = React.lazy(() => import('../../components/SideMenu/SideMenu'));
const PageHeader = React.lazy(() => import('../../components/PageHeader/PageHeader'));

function MailMergeTemplates() {
    const dispatch = useDispatch();

    const listKey = '/mailMergeTemplates';
    const savedListState = getListState(listKey) || {};

    const { addAlert } = useAlertStore();
    const { listRecords, deleteRecord, data, clearData } = useMailMergeTemplateStore();
    const { listRecords: listCategoryData, data: categoryData } = useMailMergeCategoryStore();

    const [isMounted, setIsMounted] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [listData, setListData] = useState([]);

    const pagePermission = constants.PERMISSION_CONTENT_MAILMERGETEMPLATES;
    const [hasPermission, setHasPermission] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(savedListState.categoryId || '');
    
    const selectedHandler = (mailMerge) => {
        navigate(`/mailMerge/edit/${mailMerge.Id}`);
    };

    const addedHandler = () => {
        navigate(`/mailMerge/new`);
    };

    const deletedHandler = (mailMerge) => {
        deleteRecord({ mailMergeTemplateId: mailMerge.Id });
    };

    const refreshedHandler = () => {
        listRecords();
    };

    const moreFilters = (categoryOptions) => {
        return (
            <>
                <div style={{ paddingLeft: '1%' }}>
                    <InputGroup style={{ width: '270px' }}>
                        <InputGroup.Addon>Category:</InputGroup.Addon>
                        <FormInput id="categoryId"
                            type="select"
                            required={false}
                            labelText=''
                            errorMessage={''}
                            changed={(id, value) => changedHandler(id, value)}
                            value={selectedCategory}
                        >
                            {categoryOptions}
                        </FormInput>
                    </InputGroup>
                </div>
            </>
        )
    }
    
    const changedHandler = (id, value) => {
        if (id === 'categoryId') {
            const categoryId = value ? parseInt(value) : '';
            setSelectedCategory(categoryId);
            saveListState(listKey, { categoryId });
            listRecords();
        }
    }

    useEffect(() => {
        if (!hasPermission) { return; }
        if (isMounted) { return; }

        dispatch(breadcrumbAddRoute({ key: 'mmList', text: 'Mail Merge Templates', path: window.location.pathname }));

        listRecords();
        listCategoryData();

        setIsMounted(true);
    }, [isMounted, hasPermission, listRecords, dispatch]);

    useEffect(() => {
        if (!_.isEmpty(data)) {
            if (data.status === 'fail' || data.status === 'error') {
                addAlert({ key: constants.ALERT_SERVER_ERROR, text: <strong>{data.body}</strong>, type: constants.ALERT_TYPE_DANGER });
                clearData();
            }

            if (data.scope === 'delete' && data.status === 'success') {
                listRecords();
            }

            if (data.status === 'success' && data.scope === 'list') {
                if (selectedCategory) {
                    const listDataFiltered = data.body.filter((item) => item.MailMergeCategoryId === selectedCategory);
                    setListData(listDataFiltered);
                } else {
                    setListData(data.body);
                }
            }

            setIsBusy(data.status === 'processing' && (data.scope === 'delete' || data.scope === 'list'));
        }
    }, [data, addAlert, clearData, listRecords, selectedCategory]);

    useEffect(() => {
        if (!_.isEmpty(categoryData) && categoryData.scope === 'list' && categoryData.status === 'success') {
            setCategoryOptions(OptionsHelper({ list: categoryData.body, keyField: 'Id', nameField: 'Name', defaultOptions: [['', 'All']], includeBlank: false }));
        }
    }, [categoryData]);

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveRoute());
        };
    }, [dispatch]);

    return (
        <>
            <SideMenu activeKey="content" />
            <div className="content-body">
                <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit} />
                {hasPermission && isMounted && categoryOptions &&
                    <Suspense fallback={<Spinner text="Loading..." />}>
                        <div style={{ paddingRight: '15px' }}>
                            <PageHeader title="Mail Merge Templates" />
                            <Panel>
                                <Panel.Body>
                                    <DocumentSetupList
                                        compType="Mail Merge Template"
                                        listData={listData}
                                        isBusy={isBusy}
                                        sorting={sorting}
                                        columns={columns}
                                        hideCopy={true}
                                        hidePrint={true}
                                        hideRefresh={true}
                                        activeValue={true}
                                        activeField="IsActive"
                                        moreFilters={moreFilters(categoryOptions)}
                                        added={() => addedHandler()}
                                        selected={(mailMerge) => selectedHandler(mailMerge)}
                                        deleted={(mailMerge) => deletedHandler(mailMerge)}
                                        refreshed={() => refreshedHandler()}
                                        permissionEdit={canEdit}
                                    />
                                </Panel.Body>
                            </Panel>
                        </div>
                    </Suspense>
                }
            </div>
        </>
    );
}

export default withAuthentication(withLayout(MailMergeTemplates));